'use client';

import { useCallback, useEffect, useRef } from 'react';
import {
  ELEVAR_EVENT_ORDER,
  ELEVAR_VIEWPORT_TRACKING,
} from './elevar-constants';
import {
  markEventAsFired,
  shouldFireEvent,
} from './elevar-event-deduplication';
import { useElevarEvents } from './elevar-events';
import type { ElevarImpression } from './elevar-types';

interface SearchViewportTrackingOptions {
  /**
   * List name for tracking (e.g., 'search results')
   */
  listName: string;
  /**
   * Search data to react to changes
   */
  data?: unknown;
  /**
   * Root margin for intersection observer (default: '100px')
   */
  rootMargin?: string;
  /**
   * Threshold for intersection observer (default: 0.1)
   */
  threshold?: number;
  /**
   * Maximum number of items to batch together in one event (default: 6)
   */
  batchSize?: number;
  /**
   * Delay before sending batched impressions (default: 800ms)
   */
  batchDelay?: number;
}

/**
 * Hook specifically for tracking search result impressions
 * Uses dl_view_search_results event instead of dl_view_item_list
 */
export function useSearchViewportTracking({
  listName,
  data,
  rootMargin = ELEVAR_VIEWPORT_TRACKING.ROOT_MARGIN,
  threshold = ELEVAR_VIEWPORT_TRACKING.THRESHOLD,
  batchSize = ELEVAR_VIEWPORT_TRACKING.BATCH_SIZE,
  batchDelay = ELEVAR_VIEWPORT_TRACKING.BATCH_DELAY,
}: SearchViewportTrackingOptions) {
  const { trackViewSearchResults } = useElevarEvents();
  const observerRef = useRef<IntersectionObserver | null>(null);
  const trackedElementsRef = useRef(new Set<Element>());
  const pendingImpressionsRef = useRef<ElevarImpression[]>([]);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const isInitialBatchRef = useRef(true);
  const hasFiredinitialBatchRef = useRef(false);

  const stableTrackViewSearchResults = useCallback(
    (data: Parameters<typeof trackViewSearchResults>[0]) => {
      trackViewSearchResults(data);
    },
    [trackViewSearchResults]
  );

  // Helper function to handle initial batch deduplication
  const handleInitialBatchDeduplication = useCallback(() => {
    if (
      !shouldFireEvent('dl_view_search_results_initial', listName, {
        isInitialPageLoad: true,
      })
    ) {
      // Clear the pending impressions without firing
      pendingImpressionsRef.current = [];
      return false; // Don't fire
    }
    // Mark the initial batch as fired
    markEventAsFired('dl_view_search_results_initial', listName, {
      isInitialPageLoad: true,
    });
    hasFiredinitialBatchRef.current = true;
    return true; // Fire the event
  }, [listName]);

  const flushPendingImpressions = useCallback(() => {
    if (pendingImpressionsRef.current.length === 0) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    // Check if this is the initial batch
    if (
      isInitialBatchRef.current &&
      !hasFiredinitialBatchRef.current &&
      !handleInitialBatchDeduplication()
    ) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    stableTrackViewSearchResults({
      impressions: [...pendingImpressionsRef.current],
      list: listName,
    });
    pendingImpressionsRef.current = [];
    isInitialBatchRef.current = false; // No longer the initial batch

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, [stableTrackViewSearchResults, handleInitialBatchDeduplication, listName]);

  const addToBatch = useCallback(
    (impression: ElevarImpression) => {
      pendingImpressionsRef.current.push(impression);

      // If we reach batch size, flush immediately
      if (pendingImpressionsRef.current.length >= batchSize) {
        flushPendingImpressions();
        return;
      }

      // Otherwise, set/reset the timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(flushPendingImpressions, batchDelay);
    },
    [batchSize, batchDelay, flushPendingImpressions]
  );

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      for (const entry of entries) {
        if (
          entry.isIntersecting &&
          !trackedElementsRef.current.has(entry.target)
        ) {
          const impressionData = entry.target.getAttribute(
            'data-elevar-impression'
          );
          if (impressionData) {
            try {
              const impression: ElevarImpression = JSON.parse(impressionData);
              addToBatch(impression);
              trackedElementsRef.current.add(entry.target);
            } catch (error) {
              // biome-ignore lint/suspicious/noConsole: <explanation>
              console.warn('Failed to parse Elevar impression data:', error);
            }
          }
        }
      }
    },
    [addToBatch]
  );

  useEffect(() => {
    observerRef.current = new IntersectionObserver(handleIntersection, {
      root: null,
      rootMargin,
      threshold,
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      // Flush any remaining impressions on cleanup
      flushPendingImpressions();
    };
  }, [handleIntersection, rootMargin, threshold, flushPendingImpressions]);

  const observe = useCallback((element: Element) => {
    if (observerRef.current && element) {
      observerRef.current.observe(element);
    }
  }, []);

  const unobserve = useCallback((element: Element) => {
    if (observerRef.current && element) {
      observerRef.current.unobserve(element);
      trackedElementsRef.current.delete(element);
    }
  }, []);

  // Clear tracked elements when list name changes
  const previousListNameRef = useRef(listName);
  useEffect(() => {
    if (previousListNameRef.current !== listName) {
      trackedElementsRef.current.clear();
      pendingImpressionsRef.current = [];
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      // Reset initial batch tracking for new page
      isInitialBatchRef.current = true;
      hasFiredinitialBatchRef.current = false;
      previousListNameRef.current = listName;
    }
  });

  // Set up viewport tracking for search result cards when they mount
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const productCards = document.querySelectorAll(
        '[data-elevar-impression]'
      );
      for (const card of productCards) {
        observe(card);
      }
    }, ELEVAR_EVENT_ORDER.USER_DATA_DELAY * 2); // Longer delay to ensure route tracker fires first

    return () => {
      clearTimeout(timeoutId);
      const productCards = document.querySelectorAll(
        '[data-elevar-impression]'
      );
      for (const card of productCards) {
        unobserve(card);
      }
    };
  }, [observe, unobserve]);

  // Re-track when search data changes (infinite scroll loads new products)
  useEffect(() => {
    if (!data) return;

    const timeoutId = setTimeout(() => {
      const productCards = document.querySelectorAll(
        '[data-elevar-impression]'
      );
      for (const card of productCards) {
        observe(card);
      }
    }, ELEVAR_EVENT_ORDER.USER_DATA_DELAY * 3); // Longer delay for DOM updates and to ensure route tracker fires first

    return () => clearTimeout(timeoutId);
  }, [data, observe]);
}
